import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AuthModal, useVkEntryAutoLogin } from "@/features/auth";
import { Content, Sidebar } from "@/widgets";
import { MiniAppNavbar } from "@/widgets/miniapp/miniapp-navbar";
import { MiniAppContent } from "@/widgets/miniapp/miniapp-content";
import { TelegramStartGate } from "@/widgets/miniapp/telegram-start-gate";
import { observePinnedMobileBrowserChrome } from "@/shared/lib/pinnedMobileChrome";
import { isTelegramMiniApp } from "@/shared/lib/telegram";
import { isMaxMiniApp } from "@/shared/lib/max";
import { isMiniAppShell } from "@/shared/lib/appShell";
import { setupInputAutoScroll } from "@/shared/lib/inputScroll";
import { captureAttributionUtmFromLocation } from "@/shared/lib/attributionUtm";
import { useServerSiteAnalytics } from "@/shared/lib/useServerSiteAnalytics";
import { useAnalyticsPageTracking } from "@/shared/lib/useAnalyticsPageTracking";
import { useYandexMetrikaPageHit } from "@/shared/lib/yandexMetrika";
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, matchRoutes, Routes, Route } from "react-router-dom";
import { getPendingSongGeneration } from "@/shared/lib/pendingSongGeneration";
import { getPendingClipAction } from "@/shared/lib/pendingClipAction";
import { getPendingReleaseOrder } from "@/shared/lib/pendingReleaseOrder";
import { resolvePaymentResume } from "@/shared/lib/paymentResume";
import { readPendingPaymentIdFromStorage, clearPendingPaymentFromStorage, reportMetrikaPurchaseOnce, } from "@/shared/lib/yandexMetrika";
import { getPublicPaymentById } from "@/shared/api/payments";
import { PAY_SUCCESS_SIGNAL_KEY, clearPaymentSuccessSignal, } from "@/shared/lib/paymentReturn";
import { AuthProvider, useAuth } from "./providers/AuthProvider";
import { LanguageProvider, useLanguage } from "./providers/LanguageProvider";
import { useSelectLanguage } from "./providers/useSelectLanguage";
import { AppEnvProvider } from "./providers/AppEnvProvider";
import { ThemeProvider, useTheme } from "./providers/ThemeProvider";
import { GenerationProvider } from "./providers/GenerationProvider";
import { GenerationModeProvider } from "./providers/GenerationModeProvider";
import { PlayerProvider } from "./providers/PlayerProvider";
import { GlobalPlayer } from "@/widgets/GlobalPlayer";
import { WebPlayer } from "@/widgets/web/web-player";
import { NotificationBell, useUnreadTabTitle, } from "@/features/notification-center";
import { usePaywallActive } from "@/shared/lib/paywallActive";
import { Toaster } from "@/shared/ui";
import { SharePage } from "@/pages/web/SharePage";
import { SongPublicPage } from "@/pages/web/SongPublicPage";
import { DebugPage } from "@/pages/dev/DebugPage";
import { INITIAL_IS_DEBUG_LAUNCH } from "@/shared/lib/debugLaunch";
import { isLandingActive } from "@/shared/lib/landingActive";
import "./styles/main.scss";
// SharePage — синхронный импорт: публичная ссылка /s/:token должна открываться
// даже если на CDN/nginx отсутствует async-чанк (иначе React.lazy ломается,
// а SPA-fallback отдаёт index.html вместо *.bundle.js → белый экран).
// SongPublicPage (`/song/:token`) — по той же причине синхронный импорт.
const AppContent = () => {
    const { colorScheme, setColorScheme } = useTheme();
    const { language } = useLanguage();
    const handleSelectLanguage = useSelectLanguage();
    const { isAuthenticated, isGuest, isFreshGuestSession, user, clearScheduledPostEmailAuthAction, flushPostEmailAuthAction, telegramAuthFailed, refreshUser, } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        captureAttributionUtmFromLocation(location.search);
    }, [location.search]);
    // Резюм создания песни после оплаты, если провайдер вернул НЕ на
    // /payment/success (частый кейс: возврат на корень/генерацию). Проверяем раз
    // за загрузку: есть незакрытый платёж И незавершённое действие пользователя
    // → узнаём статус; если оплачено — отправляем на /payment/success, где штатно
    // обновится баланс и запустится создание песни. На самой /payment/success не
    // вмешиваемся — она это делает сама.
    const paymentResumeCheckedRef = useRef(false);
    useEffect(() => {
        if (paymentResumeCheckedRef.current)
            return;
        if (location.pathname.startsWith("/payment/success"))
            return;
        const pid = readPendingPaymentIdFromStorage();
        // Ждём ровно те маркеры, которые ставятся по КЛИКУ пользователя в момент
        // 402: начатая генерация песни, клип-заказ (видеообложка/караоке/продление)
        // и отложенная заявка на релиз. Черновик формы сюда не входит — сам по себе
        // он не означает, что пользователь что-то запускал.
        if (pid == null ||
            (getPendingSongGeneration() == null &&
                getPendingClipAction() == null &&
                getPendingReleaseOrder() == null))
            return;
        paymentResumeCheckedRef.current = true;
        let cancelled = false;
        void (async () => {
            try {
                const payment = await getPublicPaymentById(pid);
                if (cancelled)
                    return;
                if (payment.status === "success") {
                    navigate("/payment/success", { replace: true });
                }
                else if (payment.status === "failed" || payment.status === "cancelled") {
                    clearPendingPaymentFromStorage();
                }
                // pending — оставляем как есть (вдруг подтвердится позже).
            }
            catch {
                /* не удалось проверить — тихо пропускаем */
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Текущий путь для асинхронного обработчика сигнала об оплате ниже: сигнал
    // приходит спустя минуты, и к этому моменту пользователь мог уйти на другую
    // страницу. Через ref, чтобы не переподписывать слушатель на каждую навигацию.
    const pathnameRef = useRef(location.pathname);
    pathnameRef.current = location.pathname;
    // Веб: оплату открываем в ОТДЕЛЬНОЙ вкладке, приложение остаётся тут. Когда
    // вкладка оплаты подтверждает платёж, она шлёт сигнал через localStorage
    // (см. paymentReturn) — ловим его в главной вкладке: обновляем баланс и, если
    // ждали оплату для генерации песни, продолжаем создание.
    useEffect(() => {
        const resumeAfterPayment = async (paymentId) => {
            if (!Number.isFinite(paymentId))
                return;
            // Куда вести — решает общий хелпер по ОПЛАЧЕННОМУ пакету (см.
            // paymentResume): маркеры в localStorage живут час и могли остаться от
            // брошенного флоу, поэтому по чужой покупке резюмить нельзя.
            let payment;
            try {
                payment = await getPublicPaymentById(paymentId);
            }
            catch {
                // Платёж не проверили — не рискуем: любой резюм здесь тратит баланс.
                return;
            }
            // Второй путь возврата с кассы: покупку в Метрику отчитывает вкладка
            // /payment/success, но она могла не успеть (закрыли до отправки) — здесь
            // подстраховываемся. Маркер отправки общий на все вкладки, поэтому
            // повторной покупки в отчётах не появится.
            reportMetrikaPurchaseOnce(payment);
            const decision = resolvePaymentResume(payment);
            // Релиз: шаг заявки живёт в URL. Если вкладка ВСЁ ЕЩЁ на форме — не
            // трогаем её, иначе потеряли бы заполненное. Но пока шла оплата,
            // пользователь мог уйти в другой раздел: тогда без навигации он остался бы
            // с оплаченным релизом и без единого намёка вернуться и нажать «Отправить»
            // — поэтому возвращаем на шаг подтверждения. Метку не потребляем: её
            // снимет сама ReleasePage (успешная отправка/уход).
            if (decision.kind === "release") {
                if (pathnameRef.current.startsWith("/release"))
                    return;
                navigate(decision.path, { replace: true });
                return;
            }
            // «Баланс» тут не навязываем: пользователь мог пополниться из любого места
            // приложения — выдёргивать его со страницы незачем (как было и раньше).
            if (decision.kind === "balance")
                return;
            navigate(decision.path, { replace: true, state: decision.state });
        };
        const onStorage = (e) => {
            if (e.key !== PAY_SUCCESS_SIGNAL_KEY || !e.newValue)
                return;
            // Значение сигнала — `<paymentId>:<ts>` (см. signalPaymentSuccess).
            const paidId = Number(e.newValue.split(":")[0]);
            clearPaymentSuccessSignal();
            clearPendingPaymentFromStorage();
            void refreshUser();
            void resumeAfterPayment(paidId);
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [navigate, refreshUser]);
    useServerSiteAnalytics(isAuthenticated);
    useAnalyticsPageTracking();
    useYandexMetrikaPageHit();
    // Префикс «(N) …» в заголовке вкладки при непрочитанных уведомлениях.
    useUnreadTabTitle();
    const [isAuthModalOpen, setAuthModalOpen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
    // Показываем дизайн мини-приложения для Telegram/MAX Mini App, мобильных
    // браузеров или узких экранов (≤ 770px). Сам предикат — в shared/lib/appShell:
    // тот же вопрос задаёт валидатор маршрутов уведомлений, и дублировать условие
    // нельзя (разъехавшись, они ломали бы навигацию только на узких окнах).
    const isMiniApp = isMiniAppShell(windowWidth);
    const routes = [
        { path: "/" },
        { path: "/user/:id" },
        { path: "/create" },
        { path: "/my-videos" },
        { path: "/my-poems" },
        { path: "/my-songs" },
        { path: "/extend-song" },
        { path: "/video-cover" },
        { path: "/karaoke" },
        { path: "/release" },
        { path: "/my-releases" },
        { path: "/my-photos" },
        { path: "/my-greetings" },
        { path: "/my-vocal-removal" },
        { path: "/my-portraits" },
        { path: "/my-clips" },
        { path: "/generate-greetings" },
        { path: "/generate-vocal-removal" },
        { path: "/generate-portrait" },
        { path: "/generate-clip" },
        { path: "/generate-video" },
        { path: "/generate-poem" },
        { path: "/generate-song" },
        { path: "/merge-photos" },
        { path: "/animate-photo" },
        { path: "/balance" },
        { path: "/referral" },
        { path: "/account" },
        { path: "/payment/success" },
        { path: "/support" },
        { path: "/faq" },
        { path: "/charts" },
        { path: "/share" },
        { path: "/ui" },
        { path: "/s/:token" },
        { path: "/song/:token" },
    ];
    // Проверяем, соответствует ли текущий путь какому-либо роуту
    const matchedRoutes = matchRoutes(routes, location.pathname);
    const hasValidRoute = matchedRoutes !== null;
    const isUserPage = location.pathname.startsWith("/user/");
    // Публичная страница-подарок (`/s/:token`) — full-screen, вне обычного шелла
    // (без сайдбара/навбара/плееров, доступна и гостю, и на мобильном браузере).
    const isSharePage = location.pathname.startsWith("/s/");
    // Публичная страница готовой песни (`/song/:token`) — full-screen, вне шелла,
    // доступна гостю. Боты MAX/VK шлют получателю ссылку на неё вместо URL аудио.
    const isSongPublicPage = location.pathname.startsWith("/song/");
    // Единый экран авторизации — отдельные «вход» и «регистрация» больше не нужны.
    const handleOpenAuthModal = () => {
        setAuthModalOpen(true);
    };
    const handleAuthModalClose = () => {
        clearScheduledPostEmailAuthAction();
        setAuthModalOpen(false);
    };
    const handleAuthSuccess = () => {
        setAuthModalOpen(false);
        flushPostEmailAuthAction();
    };
    // Заход по ссылке из ВК-бота (`?from=vk`): у ВК нет миниаппа, поэтому кнопки
    // ведут на веб-форму — сразу стартуем VK ID OAuth. Если попап заблокирован
    // (окно открывается вне жеста) — показываем обычный экран входа.
    useVkEntryAutoLogin({ onManualLogin: handleOpenAuthModal });
    const handleToggleSidebar = () => {
        setIsSidebarOpen((prev) => !prev);
    };
    // Отслеживаем изменение размера окна
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    // Принудительный dark на desktop снят — теперь пользователь сам выбирает тему
    // через ThemeSwitch (см. WelcomePage). Выбор сохраняется в localStorage
    // через ThemeProvider.
    const isRealMiniAppShell = isTelegramMiniApp() || isMaxMiniApp();
    // Лендинг активен (welcome-вариант свежего гостя ИЛИ превью `?welcome=1`) — тот
    // же предикат, что в HomeGate/Content. Гейтит рендер WelcomePage на "/" (desktop
    // Content И мобильный MiniAppContent) и скрытие сайдбара/таб-бара под лендингом.
    const landingActive = isLandingActive({
        pathname: location.pathname,
        search: location.search,
        isFreshGuestSession,
        welcomeVariant: user?.welcome_page_variant,
    });
    // Мобильный лендинг: WelcomePage на "/" в mobile-браузере (НЕ внутри реального
    // TG/MAX-шелла — там свой авторизованный UX). Таб-бар/навбар миниаппа на
    // лендинге лишний и перекрывает sticky CTA — скрываем. Раньше условие завязывали
    // на `!isGuest`, но на мобиле гостевая сессия создаётся всегда → лендинг не
    // показывался; теперь гейтим тем же landingActive, что и MiniAppContent.
    const isMiniAppWelcome = isMiniApp && !isRealMiniAppShell && landingActive;
    // Пейвол на экране → прячем плавающий колокольчик (перекрывал бы «Спец.
    // предложение» с таймером).
    const paywallActive = usePaywallActive((s) => s.active);
    useEffect(() => {
        const isTelegramMiniAppValue = isTelegramMiniApp();
        if (isMiniApp) {
            document.body.classList.add("miniapp");
            document.documentElement.classList.add("miniapp");
            // Настраиваем автоматическую прокрутку к инпутам при фокусе
            // Только для Telegram Mini App, не для обычных мобильных браузеров
            let cleanup;
            if (isTelegramMiniAppValue) {
                cleanup = setupInputAutoScroll();
            }
            return () => {
                document.body.classList.remove("miniapp");
                document.documentElement.classList.remove("miniapp");
                if (cleanup) {
                    cleanup();
                }
            };
        }
        else {
            document.body.classList.remove("miniapp");
            document.documentElement.classList.remove("miniapp");
        }
        return () => {
            document.body.classList.remove("miniapp");
            document.documentElement.classList.remove("miniapp");
        };
    }, [isMiniApp]);
    /** Статус-бар / theme-color в мобильном Safari и Chrome — всегда #101012, без «высветления» от оверлеев */
    useEffect(() => {
        if (!isMiniApp) {
            document.documentElement.style.removeProperty("background-color");
            return;
        }
        return observePinnedMobileBrowserChrome(colorScheme);
    }, [isMiniApp, colorScheme]);
    // Уведомления теперь показываются через центр уведомлений (колокольчик в
    // сайдбаре, см. features/notification-center) + доменные тосты по live-
    // событиям WS. Прежний авто-показ всего бэклога тостами удалён — он заваливал
    // экран при каждой перезагрузке.
    // Сайдбар скрыт на welcome-странице (/) для неавторизованных (гость видит
    // лендинг со своим navbar), на /ui (дев-песочница рисует свой topbar) и на
    // /user/:id — эти страницы на весь экран.
    const isUiKitPage = location.pathname === "/ui";
    // Лендинг активен → сайдбар скрыт при ЛЮБОМ auth-состоянии (в т.ч. `?welcome=1`
    // у залогиненного — иначе оставался сайдбар поверх full-bleed лендинга).
    // `landingActive` вычислен выше (общий с isMiniAppWelcome).
    const showSidebar = !isMiniApp &&
        hasValidRoute &&
        !isUserPage &&
        !isUiKitPage &&
        !landingActive &&
        (isAuthenticated || location.pathname !== "/");
    // Авторизация в TG WebApp провалилась (любая причина: юзера нет в БД,
    // упал бэк, нет initData и т.п.). В TG нет email-флоу — модалка
    // входа бесполезна, поэтому вместо MiniApp показываем заглушку:
    // «нажмите /start, иначе пишите в поддержку».
    // DEBUG-запуск (бот-команда /debug открыла мини-апп с payload-маркером):
    // рендерим только диагностическую страницу, минуя весь app-shell и любой
    // роутинг/авторизацию — чтобы снять состояние даже когда MAX-авторизация
    // сорвалась в гостя. Детект по сырому launch-URL (см. debugLaunch.ts).
    if (INITIAL_IS_DEBUG_LAUNCH) {
        return _jsx(DebugPage, {});
    }
    // Публичная страница-подарок рендерится полноэкранно, минуя весь app-shell
    // (сайдбар/навбар/плееры/miniapp-разметку). Доступна гостю и на любом экране.
    if (isSharePage) {
        return (_jsxs("div", { className: "app app-share", children: [_jsx(Toaster, {}), _jsx(Routes, { children: _jsx(Route, { path: "/s/:token", element: _jsx(SharePage, {}) }) })] }));
    }
    // Публичная страница песни — теперь ХАБ всех медиа (песня/видео/караоке/минус/
    // WAV). Layout зависит от данных (владелец-ли смотрит), а они грузятся ВНУТРИ
    // страницы (chicken-egg: App не знает owner_user_id до фетча). Поэтому App НЕ
    // навязывает `app-share`-обёртку — страница сама выбирает shell после загрузки:
    // владелец-залогинен → встроенный в сайт вид (сайдбар + «Поделиться»); гость/
    // чужой → полноэкранный standalone-хаб (как раньше). Toaster держим здесь, вне
    // Routes, чтобы тосты работали в обоих вариантах.
    if (isSongPublicPage) {
        return (_jsxs(_Fragment, { children: [_jsx(Toaster, {}), _jsx(Routes, { children: _jsx(Route, { path: "/song/:token", element: _jsx(SongPublicPage, {}) }) })] }));
    }
    if (telegramAuthFailed) {
        return (_jsxs("div", { className: "app", children: [_jsx(Toaster, {}), _jsx(TelegramStartGate, { language: language })] }));
    }
    return (_jsxs("div", { className: "app", children: [_jsx(Toaster, {}), showSidebar && (_jsx(Sidebar, { language: language, onSelectLanguage: handleSelectLanguage, onOpenLoginModal: handleOpenAuthModal, onOpenRegisterModal: handleOpenAuthModal, isOpen: isSidebarOpen, onToggle: handleToggleSidebar })), isMiniApp && !isMiniAppWelcome && (_jsx(MiniAppNavbar, { language: language, isAuthenticated: isAuthenticated, openLoginModal: handleOpenAuthModal, openRegisterModal: handleOpenAuthModal })), isMiniApp && !isMiniAppWelcome && isAuthenticated && !paywallActive && (_jsx(NotificationBell, { language: language, floating: true })), _jsx(AuthModal, { language: language, opened: isAuthModalOpen, onClose: handleAuthModalClose, onSuccess: handleAuthSuccess }), isMiniApp ? (_jsx(MiniAppContent, { language: language, 
                // Гостевой чекаут работает и в мини-апп-раскладке (мобильный браузер):
                // гость — полноценный пользователь, поэтому получает залогиненное дерево
                // роутов (/my-songs, дашборд на «/», /balance) — «пускаем» по
                // «авторизован ИЛИ гость». Без этого гость на мобилке не мог зайти в
                // «Мои песни», а «/» его редиректило на /generate-song.
                isLoggedIn: isAuthenticated || isGuest, openLoginModal: handleOpenAuthModal, openRegisterModal: handleOpenAuthModal, darkMode: colorScheme === "dark" })) : (_jsx(Content, { language: language, 
                // Гостевой чекаут (веб): гость получает полное дерево роутов
                // (/my-songs, /balance, оплата и т.д.) — «пускаем в приложение» по
                // «авторизован ИЛИ гость».
                isLoggedIn: isAuthenticated || isGuest, openLoginModal: handleOpenAuthModal, openRegisterModal: handleOpenAuthModal, darkMode: colorScheme === "dark" })), isMiniApp ? (_jsx(GlobalPlayer, { language: language })) : (_jsx(WebPlayer, { language: language }))] }));
};
const App = () => {
    return (_jsx(AppEnvProvider, { children: _jsx(AppContent, {}) }));
};
const AppWithProviders = () => {
    return (_jsx(ThemeProvider, { children: _jsx(LanguageProvider, { children: _jsx(AuthProvider, { children: _jsx(GenerationProvider, { children: _jsx(GenerationModeProvider, { children: _jsx(PlayerProvider, { children: _jsx(App, {}) }) }) }) }) }) }));
};
export default AppWithProviders;
